import React, { useEffect, useMemo, useState } from 'react';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { ScheduledAction } from '@demind-inc/core';
import { Dayjs } from 'dayjs';

import { CalendarAccountDropdown } from '../CalendarAccountDropdown';
import { useCalendarContext } from '../../../../data-access';
import './ActivitySaveMenu.scss';
import { DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';

interface ActivitySaveMenuProps {
  title?: string;
  visible: boolean;
  actionOptions?: ScheduledAction[];
  defaultStartTime?: Dayjs;
  defaultEndTime?: Dayjs;
  hideTimeSelector?: boolean;
  subChildren?: React.ReactNode;
  onClose: () => void;
  onDiscard: () => void;
  onAccept: (
    action: ScheduledAction,
    calendarId: string,
    startTime?: Dayjs,
    endTime?: Dayjs
  ) => void;
  defaultAction?: ScheduledAction;
}

const ActivitySaveMenu: React.FC<ActivitySaveMenuProps> = ({
  title,
  visible,
  actionOptions = [],
  defaultStartTime,
  defaultEndTime,
  hideTimeSelector,
  subChildren,
  onDiscard,
  onAccept,
  onClose,
  defaultAction,
}) => {
  const { t } = useTranslation();
  const [selectedAction, setSelectedAction] = useState<ScheduledAction | null>(null);
  const { calendarAccountsToCreateEvents, mainCalendar } = useCalendarContext();
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>(mainCalendar?.calendarId);
  const [startTime, setStartTime] = useState<Dayjs | null>(defaultStartTime || null);
  const [endTime, setEndTime] = useState<Dayjs | null>(defaultEndTime || null);

  const isDisabledToAccept = useMemo(
    () =>
      (!!actionOptions.length && !selectedAction) ||
      !selectedCalendarId ||
      (startTime && endTime && startTime.isAfter(endTime)),
    [selectedAction, actionOptions, selectedCalendarId, startTime, endTime]
  );
  useEffect(() => {
    if (!defaultAction) {
      return;
    }

    setSelectedAction(defaultAction);
  }, [defaultAction]);

  useEffect(() => {
    setSelectedCalendarId(mainCalendar?.calendarId);
  }, [mainCalendar?.calendarId]);

  useEffect(() => {
    if (defaultStartTime) {
      setStartTime(defaultStartTime);
    }
    if (defaultEndTime) {
      setEndTime(defaultEndTime);
    }
  }, [defaultStartTime, defaultEndTime]);

  const handleAccept = () => {
    onAccept(selectedAction, selectedCalendarId, startTime, endTime);
    setSelectedAction(null);
    onClose();
  };

  const handleDiscard = () => {
    onDiscard();
    setSelectedAction(null);
    onClose();
  };

  return (
    <Dialog
      open={visible}
      className="activity-save-menu"
      hideBackdrop
      onClose={onClose}
      key={calendarAccountsToCreateEvents[0]?.calendarId}
    >
      <DialogTitle className="activity-save-menu__header">
        <p>{title ?? t('calendar.eventMenu.chooseActivity')}</p>
        <CloseOutlined onClick={onClose} className="activity-save-menu__header__icon" />
      </DialogTitle>
      <DialogContent className="activity-save-menu__content">
        {!!actionOptions.length && (
          <div className="activity-save-menu__chip-container">
            {actionOptions.map((option) => (
              <Chip
                label={option.name}
                key={option.taskId}
                variant={selectedAction?.taskId === option.taskId ? 'filled' : 'outlined'}
                color="primary"
                onClick={() => setSelectedAction(option)}
              />
            ))}
          </div>
        )}
        {!hideTimeSelector && startTime && endTime && (
          <div className="activity-save-menu__time-selector">
            <TimePicker
              label={t('calendar.eventMenu.startTime')}
              value={startTime}
              className="activity-save-menu__time-selector__item"
              format={'h:mm A'}
              views={['hours', 'minutes']}
              onChange={(newValue) => {
                if (newValue) {
                  setStartTime(newValue);
                }
              }}
            />
            <TimePicker
              label={t('calendar.eventMenu.endTime')}
              value={endTime}
              className="activity-save-menu__time-selector__item"
              format={'h:mm A'}
              views={['hours', 'minutes']}
              onChange={(newValue) => {
                if (newValue) {
                  setEndTime(newValue);
                }
              }}
            />
          </div>
        )}
        {subChildren}
        <div className="activity-save-menu__title-container">
          <CalendarAccountDropdown
            calendars={calendarAccountsToCreateEvents}
            onSelectCalendarId={(id) => setSelectedCalendarId(id)}
            selectedCalendarId={selectedCalendarId}
          />
        </div>
      </DialogContent>
      <DialogActions className="activity-save-menu__actions">
        <div className="activity-save-menu__button-group">
          <Button
            onClick={handleDiscard}
            variant="text"
            className="activity-save-menu__button-group__button activity-save-menu__button-group__button--delete"
          >
            {t('calendar.common.discard')}
          </Button>

          <Button
            variant="contained"
            className="activity-save-menu__button-group__button activity-save-menu__button-group__button--save"
            onClick={handleAccept}
            disabled={isDisabledToAccept}
          >
            {t('calendar.eventMenu.saveAsCalendarEvent')}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ActivitySaveMenu;
